import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from '../base.entity';
import { ChatMessageKind } from './chat-enums';

/**
 * A single message or message-like event in a conversation. Soft-deleted
 * (not hard-deleted) so history remains coherent — a deleted message
 * renders as "this message was deleted" in the UI.
 */
@Entity('chat_message')
@Index('idx_chat_message_conversation', ['conversationId', 'id'])
@Index('idx_chat_message_parent', ['conversationId', 'parentMessageId'])
@Index('idx_chat_message_client_id', ['clientMessageId'])
// Retry-safety: enforce that a single (conversation, clientMessageId) pair
// is unique. Twilio webhooks, BullMQ jobs, and API-level retries all stamp
// the same `clientMessageId` on the second attempt; without this constraint
// the writers would silently produce duplicate `chat_message` rows for the
// same logical event. NULL values are allowed multiple times per MySQL's
// default unique-constraint semantics, so system messages with no dedup
// key are unaffected.
@Index(
	'uq_chat_message_convo_client_id',
	['conversationId', 'clientMessageId'],
	{ unique: true },
)
export class ChatMessageEntity extends BaseEntity {
	@Column('char', { name: 'uuid', length: 36, unique: true })
	public uuid!: string;

	@Column('int', {
		name: 'fk_chat_conversation__id',
		unsigned: true,
	})
	public conversationId!: number;

	/** Null for system messages (automation, join/leave notices). */
	@Column('int', {
		name: 'fk_chat_participant__id_sender',
		nullable: true,
		unsigned: true,
	})
	public senderParticipantId?: number;

	@Column('enum', {
		name: 'kind',
		enum: ChatMessageKind,
		default: ChatMessageKind.Text,
	})
	public kind!: ChatMessageKind;

	@Column('mediumtext', { name: 'content', nullable: true })
	public content?: string;

	/** Thread parent — null for top-level messages. */
	@Column('int', {
		name: 'fk_chat_message__id_parent',
		nullable: true,
		unsigned: true,
	})
	public parentMessageId?: number;

	/**
	 * FK into `_file` — the canonical file record the rest of the app
	 * already uses (client_file, order_file, invoice_file, etc. all
	 * join through it). Reusing this means chat attachments land in
	 * the same public/private bucket split, go through the same
	 * presigned-upload flow, and surface in generic file tooling for
	 * free.
	 *
	 * Name, content-type, size, and S3 key all live on `_file` — chat
	 * doesn't duplicate any of them.
	 */
	@Column('int', {
		name: 'fk_file__id',
		nullable: true,
		unsigned: true,
	})
	public fkFileId?: number;

	/**
	 * Mentioned participant ids. Stored as JSON array of numeric participantIds
	 * (not user ids — so @mentions work consistently across DMs/groups even
	 * when the mentioned user later leaves the conversation).
	 */
	@Column('json', { name: 'mentions', nullable: true })
	public mentions?: Array<number>;

	/**
	 * Emoji reactions. Stored as JSON map: { "👍": [participantId1, ...], ... }.
	 * Kept denormalized rather than in a reactions table because reactions are
	 * always fetched with the message and never queried independently.
	 */
	@Column('json', { name: 'reactions', nullable: true })
	public reactions?: Record<string, Array<number>>;

	@Column('datetime', { name: 'editedAt', nullable: true, precision: 3 })
	public editedAt?: Date;

	@Column('datetime', { name: 'softDeletedAt', nullable: true, precision: 3 })
	public softDeletedAt?: Date;

	/** Dedup key from outbox envelope — ignored on second attempt within window. */
	@Column('char', {
		name: 'clientMessageId',
		length: 36,
		nullable: true,
	})
	public clientMessageId?: string;

	@Column('json', { name: 'metadata', nullable: true })
	public metadata?: Record<string, unknown>;
}
